/**
 * Font resolution shared by the browser platform (text measuring) and the Canvas renderer (text
 * drawing): both MUST build the same CSS font string, otherwise layout and drawing disagree.
 *
 * Flash font names (TextFormat.font, e.g. "Tahoma", "_sans") map to a CSS family stack:
 *   1. the embedded font exported by the extraction pipeline (FontDef.file registered through the
 *      FontFace API under FontDef.cssFamily), only when one exists for that name AND style — an
 *      embedded bold subset is never used for regular text (the browser would take the bold glyphs
 *      it contains and fall back to regular system glyphs for the rest: mixed weights per letter);
 *   2. the system font of that name and metric-compatible substitutes;
 *   3. a generic family.
 * Glyphs missing from an embedded subset fall back to the next family per character (same style).
 * PORT: an embedded font that is only a subset of its face (FontDef.partial: the SWF's bold Arial holds eleven letters)
 * comes after the system font of its name: a device with that font (or an alias of it: Liberation Sans on Linux,
 * Roboto on Android) draws every letter of a word with one face, the subset only fills in where the device has
 * none (every letter of a word from one font, not a few from the subset and the rest from the fallback, which at
 * small sizes on a phone looked like capitals among lowercase letters).
 *
 * DOM-free at import time (usable in Node tests); `loadFontFaces` needs the FontFace API.
 */
import type { FontMetrics, FontSpec } from './types';

/** Font entry as produced by AssetLibrary.fonts (see src/flash/symbols/AssetLibrary.ts FontInfo). */
export interface EmbeddedFont {
  name: string;
  bold: boolean;
  italic: boolean;
  cssFamily: string;
  url: string | null;
  /** Only a subset of its face (FontDef.partial): after the system font of its name. */
  partial?: boolean;
}

/** System font stacks for the Flash device fonts / fonts used by the game. */
const SYSTEM_STACKS: Record<string, string[]> = {
  _sans: ['Arial', 'Liberation Sans', 'Helvetica', 'sans-serif'],
  _serif: ['Times New Roman', 'Liberation Serif', 'Times', 'serif'],
  _typewriter: ['Courier New', 'Liberation Mono', 'Courier', 'monospace'],
  arial: ['Arial', 'Liberation Sans', 'Helvetica', 'sans-serif'],
  'arial black': ['Arial Black', 'Arial', 'Liberation Sans', 'sans-serif'],
  // Tahoma is a narrow Verdana: Arial / Liberation Sans match its advance widths far better than
  // Verdana / DejaVu Sans (~15% wider), so UI text sized for Tahoma keeps its line breaks.
  tahoma: ['Tahoma', 'Arial', 'Liberation Sans', 'Verdana', 'DejaVu Sans', 'sans-serif'],
  verdana: ['Verdana', 'DejaVu Sans', 'Geneva', 'sans-serif'],
  // DejaVu Sans has nearly the same advance widths as Lucida Sans Unicode (digits 0.636 vs 0.632 em).
  'lucida sans unicode': ['Lucida Sans Unicode', 'Lucida Grande', 'DejaVu Sans', 'sans-serif'],
  'times new roman': ['Times New Roman', 'Liberation Serif', 'Times', 'serif'],
  'courier new': ['Courier New', 'Liberation Mono', 'Courier', 'monospace'],
  georgia: ['Georgia', 'serif'],
  'trebuchet ms': ['Trebuchet MS', 'sans-serif'],
};

const GENERIC = new Set(['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui']);

/**
 * Vertical metrics (em fractions) of the fonts the game uses, from the fonts' OS/2 usWinAscent /
 * usWinDescent (what Flash's Windows player uses for device text). Using the Flash font's metrics
 * keeps line heights Flash-like even when the browser substitutes another font.
 * UNVERIFIED: values transcribed from the fonts' tables, not measured in Flash Player.
 */
const METRICS: Record<string, [number, number]> = {
  tahoma: [2049 / 2048, 423 / 2048],
  arial: [1854 / 2048, 434 / 2048],
  _sans: [1854 / 2048, 434 / 2048],
  'arial black': [2254 / 2048, 634 / 2048],
  verdana: [2059 / 2048, 430 / 2048],
  'lucida sans unicode': [2246 / 2048, 901 / 2048],
  'times new roman': [1825 / 2048, 443 / 2048],
  _serif: [1825 / 2048, 443 / 2048],
  'courier new': [1705 / 2048, 615 / 2048],
  _typewriter: [1705 / 2048, 615 / 2048],
};

function quoteFamily(f: string): string {
  if (GENERIC.has(f)) return f;
  return '"' + f.replace(/["\\]/g, '') + '"';
}

export class FontResolver {
  private embedded: EmbeddedFont[] = [];
  private stacks = new Map<string, string>();
  /** Increments whenever the registered fonts change (caches keyed on it). */
  version = 0;

  /** Register embedded fonts (AssetLibrary.fonts). Entries without a file URL are ignored. */
  register(fonts: readonly EmbeddedFont[]): void {
    for (const f of fonts) {
      if (!f.url) continue;
      if (this.embedded.some((e) => e.cssFamily === f.cssFamily)) continue;
      this.embedded.push({ ...f });
    }
    this.stacks.clear();
    this.version++;
  }

  get embeddedFonts(): readonly EmbeddedFont[] {
    return this.embedded;
  }

  /** CSS font-family list for a Flash font name and style. */
  familyStack(name: string, bold: boolean, italic: boolean): string {
    const key = `${name}\u0000${bold ? 1 : 0}${italic ? 1 : 0}`;
    let s = this.stacks.get(key);
    if (s !== undefined) return s;
    const families: string[] = [];
    const lname = (name || '_sans').trim().toLowerCase();
    const own = this.embedded.filter((f) => f.name.trim().toLowerCase() === lname && f.bold === bold && f.italic === italic);
    for (const f of own) if (!f.partial) families.push(f.cssFamily);
    const sys = SYSTEM_STACKS[lname] ?? [...(name && !name.startsWith('_') ? [name] : []), 'sans-serif'];
    // A subset right after the system font of its name (a device font takes a whole word; the subset what it lacks).
    const partial = own.filter((f) => f.partial).map((f) => f.cssFamily);
    families.push(...sys.slice(0, 1), ...partial, ...sys.slice(1));
    s = [...new Set(families)].map(quoteFamily).join(', ');
    this.stacks.set(key, s);
    return s;
  }

  /** Full CSS `font` shorthand for a FontSpec (size in px). */
  cssFont(font: FontSpec): string {
    const size = font.size > 0 ? font.size : 12;
    return `${font.italic ? 'italic ' : ''}${font.bold ? 'bold ' : ''}${size}px ${this.familyStack(font.family, font.bold, font.italic)}`;
  }

  /** Flash-like vertical metrics for known fonts, else null (measure from the host font). */
  knownMetrics(font: FontSpec): FontMetrics | null {
    const m = METRICS[(font.family || '_sans').trim().toLowerCase()];
    return m ? { ascent: font.size * m[0], descent: font.size * m[1] } : null;
  }
}

/**
 * Register embedded fonts through the FontFace API and wait until they are usable.
 * Failures (missing file, unsupported format) are reported and leave the system fallback in place.
 */
export async function loadFontFaces(fonts: readonly EmbeddedFont[], onError?: (font: EmbeddedFont, err: unknown) => void): Promise<number> {
  const g: any = globalThis as any;
  const FontFaceCtor = g.FontFace;
  const set = g.document?.fonts ?? g.fonts;
  if (typeof FontFaceCtor !== 'function' || !set) return 0;
  let loaded = 0;
  await Promise.all(
    fonts.map(async (f) => {
      if (!f.url) return;
      try {
        const face = new FontFaceCtor(f.cssFamily, `url("${f.url}")`, { weight: f.bold ? '700' : '400', style: f.italic ? 'italic' : 'normal' });
        await face.load();
        set.add(face);
        loaded++;
      } catch (err) {
        if (onError) onError(f, err);
        // eslint-disable-next-line no-console
        else console.warn(`[flash] font ${f.name} (${f.url}) could not be loaded; using system fallback`, err);
      }
    }),
  );
  return loaded;
}
